import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
const DEFAULT_CACHE_TTL = 30 * 60 * 1000; // 30 Minuten
export function useGoogleReviews(options) {
    const { apiClient, target, consentGranted = false, cacheKey = 'google-reviews', cacheTtlMs = DEFAULT_CACHE_TTL, rootMargin = '0px', threshold = 0.1, once = true } = options;
    const isLoading = ref(false);
    const error = ref(null);
    const reviews = ref([]);
    const meta = ref(null);
    const hasFetched = ref(false);
    const observer = ref(null);
    const resolveConsent = () => {
        if (typeof consentGranted === 'boolean') {
            return consentGranted;
        }
        return consentGranted.value;
    };
    const loadFromCache = () => {
        if (typeof window === 'undefined')
            return null;
        try {
            const raw = window.sessionStorage.getItem(cacheKey);
            if (!raw)
                return null;
            const cached = JSON.parse(raw);
            // Check if cache is expired by time
            if (Date.now() - cached.timestamp > cacheTtlMs) {
                console.log('[useGoogleReviews] Cache expired by time');
                window.sessionStorage.removeItem(cacheKey);
                return null;
            }
            return cached.payload;
        }
        catch (cacheError) {
            console.warn('[useGoogleReviews] Failed to read cache entry', cacheError);
            return null;
        }
    };
    const saveToCache = (payload) => {
        if (typeof window === 'undefined')
            return;
        try {
            const cached = {
                timestamp: Date.now(),
                payload
            };
            window.sessionStorage.setItem(cacheKey, JSON.stringify(cached));
            console.log('[useGoogleReviews] 💾 Saved to cache with settings:', payload.settings);
        }
        catch (cacheError) {
            console.warn('[useGoogleReviews] Failed to persist cache entry', cacheError);
        }
    };
    const invalidateCacheIfSettingsChanged = (newPayload) => {
        const cachedPayload = loadFromCache();
        if (!cachedPayload || !cachedPayload.settings || !newPayload.settings) {
            return false;
        }
        const settingsChanged = cachedPayload.settings.minRating !== newPayload.settings.minRating ||
            cachedPayload.settings.maxDisplayCount !== newPayload.settings.maxDisplayCount;
        if (settingsChanged) {
            console.log('[useGoogleReviews] 🔄 Settings changed, invalidating cache', {
                old: cachedPayload.settings,
                new: newPayload.settings
            });
            window.sessionStorage.removeItem(cacheKey);
            return true;
        }
        return false;
    };
    const applyPayload = (payload) => {
        if (!payload)
            return;
        meta.value = {
            lastFetchedAt: payload.lastFetchedAt,
            reviewCount: payload.reviewCount,
            totalReviewCount: payload.totalReviewCount,
            averageRating: payload.averageRating,
            projectId: payload.projectId,
            projectName: payload.projectName,
            placeId: payload.placeId,
            isStale: payload.isStale,
            consentRequired: payload.consentRequired
        };
        reviews.value = payload.reviews || [];
    };
    const fetchReviews = async () => {
        const cachedCheck = loadFromCache();
        console.log('🔍 [useGoogleReviews] fetchReviews called', {
            isLoading: isLoading.value,
            hasFetched: hasFetched.value,
            consent: resolveConsent(),
            cacheKey,
            hasCachedData: !!cachedCheck,
            cachedReviewCount: cachedCheck?.reviews?.length || 0,
            currentReviewCount: reviews.value.length
        });
        if (isLoading.value || hasFetched.value) {
            console.log('⏭️ [useGoogleReviews] Skipping fetch - already loading or fetched', {
                reason: isLoading.value ? 'loading' : 'fetched',
                hasCachedData: !!cachedCheck,
                currentReviewCount: reviews.value.length
            });
            return;
        }
        if (!resolveConsent()) {
            console.log('🚫 [useGoogleReviews] No consent granted');
            error.value = null;
            return;
        }
        const cached = loadFromCache();
        if (cached) {
            console.log('💾 [useGoogleReviews] Found cached data', {
                hasReviews: !!cached.reviews && cached.reviews.length > 0,
                reviewCount: cached.reviews?.length || 0,
                lastFetchedAt: cached.lastFetchedAt
            });
            // Only use cache if it contains actual reviews
            // Empty cache means the API has no data yet (no placeId configured or no reviews)
            if (cached.reviews && cached.reviews.length > 0) {
                console.log('✅ [useGoogleReviews] Using cached reviews');
                applyPayload(cached);
                hasFetched.value = true;
                return;
            }
            else {
                console.log('📭 [useGoogleReviews] Cache is empty - fetching fresh from API');
                // Continue to API fetch below
            }
        }
        try {
            isLoading.value = true;
            error.value = null;
            console.log('🌐 [useGoogleReviews] Fetching reviews from API...');
            const response = await apiClient.getGoogleReviews(true);
            console.log('📦 [useGoogleReviews] API Response:', response);
            if (response.status === 'success' && response.data) {
                // Check if settings changed and invalidate cache if needed
                invalidateCacheIfSettingsChanged(response.data);
                applyPayload(response.data);
                saveToCache(response.data);
                hasFetched.value = true;
                console.log('✅ [useGoogleReviews] Reviews loaded successfully', {
                    count: response.data.reviews?.length || 0,
                    settings: response.data.settings
                });
            }
            else {
                error.value = response.message || 'Bewertungen konnten nicht geladen werden.';
                console.error('❌ [useGoogleReviews] Failed to load reviews:', error.value);
            }
        }
        catch (fetchError) {
            console.error('[useGoogleReviews] Failed to fetch reviews', fetchError);
            error.value = fetchError.message || 'Bewertungen konnten nicht geladen werden.';
        }
        finally {
            isLoading.value = false;
        }
    };
    const handleIntersect = (entries, obs) => {
        console.log('👁️ [useGoogleReviews] IntersectionObserver triggered', {
            entries: entries.map(e => ({
                isIntersecting: e.isIntersecting,
                intersectionRatio: e.intersectionRatio,
                target: e.target.tagName,
                boundingClientRect: e.boundingClientRect,
                rootBounds: e.rootBounds
            }))
        });
        for (const entry of entries) {
            console.log('🔍 [useGoogleReviews] Checking entry:', {
                isIntersecting: entry.isIntersecting,
                ratio: entry.intersectionRatio,
                boundingRect: entry.boundingClientRect
            });
            if (entry.isIntersecting) {
                console.log('✨ [useGoogleReviews] Element is visible - fetching reviews');
                fetchReviews();
                if (once) {
                    obs.disconnect();
                    observer.value = null;
                    console.log('🔌 [useGoogleReviews] Observer disconnected (once mode)');
                }
                break;
            }
            else {
                console.log('❌ [useGoogleReviews] Element is NOT intersecting - not fetching');
            }
        }
    };
    const cleanupObserver = () => {
        if (observer.value) {
            observer.value.disconnect();
            observer.value = null;
        }
    };
    const initObserver = () => {
        cleanupObserver();
        if (!target.value) {
            console.log('⚠️ [useGoogleReviews] No target element for observer');
            return;
        }
        if (!resolveConsent()) {
            console.log('⚠️ [useGoogleReviews] No consent - observer not initialized');
            return;
        }
        console.log('🔭 [useGoogleReviews] Initializing IntersectionObserver', {
            rootMargin,
            threshold,
            targetElement: target.value.tagName
        });
        observer.value = new IntersectionObserver(handleIntersect, {
            rootMargin,
            threshold
        });
        observer.value.observe(target.value);
        console.log('👀 [useGoogleReviews] Observer attached to element');
    };
    // Watch consent changes
    if (typeof consentGranted !== 'boolean') {
        watch(consentGranted, (granted) => {
            console.log('🔐 [useGoogleReviews] Consent changed:', granted);
            if (granted) {
                if (hasFetched.value) {
                    console.log('✅ [useGoogleReviews] Already fetched - skipping');
                    return;
                }
                // Try cached payload first
                const cached = loadFromCache();
                if (cached) {
                    console.log('💾 [useGoogleReviews] Using cached data from consent change', {
                        hasReviews: !!cached.reviews && cached.reviews.length > 0,
                        reviewCount: cached.reviews?.length || 0,
                        placeId: cached.placeId
                    });
                    applyPayload(cached);
                    // Only mark as fetched if we actually have reviews
                    // Otherwise, allow IntersectionObserver to trigger a fresh fetch
                    if (cached.reviews && cached.reviews.length > 0) {
                        hasFetched.value = true;
                        console.log('✅ [useGoogleReviews] Cache has reviews - marking as fetched');
                    }
                    else {
                        console.log('📭 [useGoogleReviews] Cache is empty - allowing fresh fetch');
                    }
                    return;
                }
                // Check if element is already visible
                if (target.value) {
                    const rect = target.value.getBoundingClientRect();
                    const isVisible = (rect.top >= 0 &&
                        rect.left >= 0 &&
                        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                        rect.right <= (window.innerWidth || document.documentElement.clientWidth));
                    console.log('👁️ [useGoogleReviews] Element visibility check:', {
                        isVisible,
                        rect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
                        viewport: { height: window.innerHeight, width: window.innerWidth }
                    });
                    if (isVisible) {
                        // Element is already visible, fetch immediately
                        console.log('🚀 [useGoogleReviews] Element visible - fetching immediately');
                        fetchReviews();
                    }
                    else {
                        // Element not visible, setup observer
                        console.log('⏳ [useGoogleReviews] Element not visible - setting up observer');
                        initObserver();
                    }
                }
                else {
                    console.log('⏳ [useGoogleReviews] No target element yet - setting up observer');
                    initObserver();
                }
            }
            else {
                // Reset state if consent revoked
                console.log('🔒 [useGoogleReviews] Consent revoked - resetting state');
                reviews.value = [];
                meta.value = null;
                hasFetched.value = false;
                cleanupObserver();
            }
        }, { immediate: true });
    }
    onMounted(() => {
        console.log('🎬 [useGoogleReviews] Component mounted', {
            hasTarget: !!target.value,
            consentType: typeof consentGranted,
            consentValue: resolveConsent()
        });
        if (typeof consentGranted === 'boolean') {
            if (!consentGranted) {
                console.log('🚫 [useGoogleReviews] Consent not granted (boolean mode)');
                return;
            }
            const cached = loadFromCache();
            if (cached) {
                console.log('💾 [useGoogleReviews] Using cached data on mount');
                applyPayload(cached);
                hasFetched.value = true;
                return;
            }
        }
        initObserver();
    });
    onBeforeUnmount(() => {
        cleanupObserver();
    });
    return {
        isLoading,
        error,
        reviews,
        meta,
        hasFetched,
        refetch: async () => {
            hasFetched.value = false;
            cleanupObserver();
            if (resolveConsent()) {
                await fetchReviews();
            }
        }
    };
}
